import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { enableMapSet } from 'immer';
import { StateCreator, StoreApi, UseBoundStore, create } from 'zustand';
import {
  DevtoolsOptions,
  type PersistStorage,
  devtools,
  persist,
} from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// Enable Map and Set support for immer (must be called before any stores are created)
// This must be called at module load time, before any stores are created
enableMapSet();

export interface CreateStorePersistOptions<T> {
  /** Storage key (defaults to store name when not set) */
  name?: string;
  /** Custom storage backend (e.g. sessionStorage via createJSONStorage). When partialize is used, persisted state is Partial<T> so storage is typed accordingly. */
  storage?: PersistStorage<Partial<T>>;
  /** Limit which state is persisted */
  partialize?: (state: T) => Partial<T>;
  /** Called after rehydration; use to notify app when persisted state is restored */
  onRehydrateStorage?: (
    state: T
  ) => ((state?: T, error?: unknown) => void) | void;
  /** Custom merge when combining persisted state with current state */
  merge?: (persistedState: unknown, currentState: T) => T;
}

export function createStore<T extends object>(
  name: string,
  initializer: StateCreator<T, [['zustand/immer', never]], []>,
  options?: {
    devtools?: Omit<DevtoolsOptions, 'name'>;
    persisted?: boolean;
    persist?: CreateStorePersistOptions<T>;
  }
) {
  const withImmer = immer<T>(initializer);
  const withDevtool = devtools(withImmer, { name, ...options?.devtools });
  const createdStore = options?.persisted
    ? create(
        persist(withDevtool, {
          name: options.persist?.name ?? name,
          ...(options.persist?.storage && {
            storage: options.persist.storage,
          }),
          ...(options.persist?.partialize && {
            partialize: options.persist.partialize,
          }),
          ...(options.persist?.onRehydrateStorage && {
            onRehydrateStorage: options.persist.onRehydrateStorage,
          }),
          ...(options.persist?.merge && {
            merge: options.persist.merge,
          }),
        })
      )
    : create(withDevtool);
  const withSelectors = createSelectorHooks(
    createdStore as UseBoundStore<StoreApi<T>>
  );
  // Expose raw store for subscribe/getState (e.g. URL sync that must react after rehydration)
  return Object.assign(withSelectors, { __store: createdStore });
}
